import type { HubSpotIdentifyPayload } from '../types/hubspot.js';
import { getTrackingData } from '../cookie/tracking-cookie.js';
import { getTaleezUser } from '../cookie/taleez-user.js';
import { hashEmail } from '../utils/hash.js';

/**
 * Construit le payload _hsq identify depuis le cookie tracking.
 *
 * Règles :
 * - Email : priorité taleezUser.mail > email_identify_hs > email
 * - first_* : depuis first_touch, uniquement si non vide
 * - last_* : depuis last_touch, uniquement si non vide
 * - UTM courants = last_touch (rétrocompatibilité HubSpot)
 * - Click IDs depuis last_touch
 * - Page views : poussés même si 0 (test !== undefined)
 * - amplitude_user_id : hash SHA-256 de l'email (bridge HubSpot ↔ Amplitude)
 * - Les strings vides ne sont PAS envoyées (HubSpot écraserait les vraies valeurs)
 *
 * Async car le hash email utilise Web Crypto API.
 */
export async function buildHubSpotIdentifyPayload(
  context: 'boot' | 'submit' | 'thankyou' = 'boot',
): Promise<HubSpotIdentifyPayload> {
  const data = getTrackingData();
  const first = data.first_touch;
  const last = data.last_touch;
  const payload: HubSpotIdentifyPayload = {};

  // Identity — priorité taleezUser.mail > email_identify_hs > email
  const taleezUser = getTaleezUser();
  const email = taleezUser?.mail ?? data.email_identify_hs ?? data.email;
  if (email) payload.email = email;
  //if (data.phone) payload.phone = data.phone;

  // First touch → first_*
  if (first) {
    if (first.utm_source) payload.first_utm_source = first.utm_source;
    if (first.utm_medium) payload.first_utm_medium = first.utm_medium;
    if (first.utm_campaign) payload.first_utm_campaign = first.utm_campaign;
    if (first.utm_term) payload.first_utm_term = first.utm_term;
    if (first.utm_content) payload.first_utm_content = first.utm_content;
    if (first.referrer) payload.first_referrer = first.referrer;
    if (first.landing_page) payload.first_landing_page = first.landing_page;
    if (first.website_source) payload.first_website_source = first.website_source;
  }

  // Last touch → last_*
  if (last) {
    if (last.utm_source) payload.last_utm_source = last.utm_source;
    if (last.utm_medium) payload.last_utm_medium = last.utm_medium;
    if (last.utm_campaign) payload.last_utm_campaign = last.utm_campaign;
    if (last.utm_term) payload.last_utm_term = last.utm_term;
    if (last.utm_content) payload.last_utm_content = last.utm_content;
    if (last.referrer) payload.last_referrer = last.referrer;
    if (last.landing_page) payload.last_landing_page = last.landing_page;
    if (last.website_source) payload.last_website_source = last.website_source;
  }

  // UTM courants = last touch (rétrocompatibilité HubSpot)
  if (last) {
    if (last.utm_source) payload.utm_source = last.utm_source;
    if (last.utm_medium) payload.utm_medium = last.utm_medium;
    if (last.utm_campaign) payload.utm_campaign = last.utm_campaign;
    if (last.utm_term) payload.utm_term = last.utm_term;
    if (last.utm_content) payload.utm_content = last.utm_content;
  }

  // Click IDs (depuis last touch)
  if (last) {
    if (last.gclid) payload.gclid = last.gclid;
    if (last.msclkid) payload.msclkid = last.msclkid;
    if (last.li_fat_id) payload.li_fat_id = last.li_fat_id;
    if (last.fbclid) payload.fbclid = last.fbclid;
  }

  // Amplitude device ID
  if (data.amplitude_device_id) payload.amplitude_device_id = data.amplitude_device_id;

  // Amplitude user ID bridge (hash du même email que celui utilisé pour setUserId)
  const hash = await hashEmail(email);
  if (hash) payload.amplitude_user_id = hash;

  // Page views (poussés même si 0 — test !== undefined)
  if (data.page_view_session_public !== undefined) payload.page_view_session_public = data.page_view_session_public;
  if (data.page_view_session_blog !== undefined) payload.page_view_session_blog = data.page_view_session_blog;
  if (data.page_view_session_dashboard !== undefined) payload.page_view_session_dashboard = data.page_view_session_dashboard;
  if (data.page_view_session_totale !== undefined) payload.page_view_session_totale = data.page_view_session_totale;
  if (data.firstpage_view) payload.firstpage_view = data.firstpage_view;

  // cookie_attribution : snapshot JSON brut du cookie, debug/audit.
  // Uniquement au submit (au boot/TY le cookie est moins riche, on n'écrase pas).
  if (context === 'submit') {
    payload.cookie_attribution = JSON.stringify(data);
  }

  return payload;
}
